import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, Send, Mic, MicOff, Users, Gavel, DollarSign,
  Clock, MessageSquare, Volume2, VolumeX, AlertTriangle, Info, CheckCircle,
  Sparkles, Pause, Play, Trash2, Plus, Eye, TrendingUp
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/imageUrl';
import { formatTimeIST } from '../../utils/timezone';

const AuctionRoom = () => {
  const { auctionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [popups, setPopups] = useState([]);
  const [connectedUsers, setConnectedUsers] = useState(0);
  const [ws, setWs] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isAnnouncing, setIsAnnouncing] = useState(false);
  
  // Popup form
  const [showPopupForm, setShowPopupForm] = useState(false);
  const [popupForm, setPopupForm] = useState({
    title: '', message: '', type: 'info', duration: 10
  });
  
  // Custom announcement
  const [customMessage, setCustomMessage] = useState('');
  
  const audioRef = useRef(null);

  const fetchRoomData = useCallback(async () => {
    try {
      const response = await api.get(`/admin/auctions/${auctionId}/room`);
      setAuction(response.data.auction);
      setBids(response.data.bids || []);
      setPopups(response.data.popups || []);
      setConnectedUsers(response.data.connected_users || 0);
    } catch (error) {
      console.error('Failed to fetch room data:', error);
      toast.error('Failed to load auction room');
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  useEffect(() => {
    fetchRoomData();
    const interval = setInterval(fetchRoomData, 5000);
    return () => clearInterval(interval);
  }, [fetchRoomData]);

  // WebSocket connection
  useEffect(() => {
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://')}/api/auction/ws/${auctionId}`;
    const socket = new WebSocket(wsUrl);
    
    socket.onopen = () => {
      console.log('WebSocket connected');
    };
    
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'new_bid') {
        setBids(prev => [data.bid, ...prev]);
        setAuction(prev => ({
          ...prev,
          current_bid: data.current_bid,
          total_bids: (prev?.total_bids || 0) + 1
        }));
      } else if (data.type === 'popup') {
        setPopups(prev => [data.popup, ...prev]);
      } else if (data.type === 'voice_announcement' && audioEnabled) {
        playAudio(data.audio);
      }
    };
    
    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    setWs(socket);
    
    return () => {
      socket.close();
    };
  }, [auctionId, audioEnabled]);

  const playAudio = (base64Audio) => {
    if (audioRef.current) {
      audioRef.current.src = `data:audio/mp3;base64,${base64Audio}`;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  };

  const sendAnnouncement = async (type) => {
    setIsAnnouncing(true);
    try {
      const payload = { type };
      if (type === 'custom' && customMessage) {
        payload.message = customMessage;
      }
      
      const response = await api.post(`/admin/auctions/${auctionId}/announce`, payload);
      if (response.data.success) {
        toast.success('Announcement sent!');
        if (response.data.audio && audioEnabled) {
          playAudio(response.data.audio);
        }
        setCustomMessage('');
      } else {
        toast.error('Failed to generate announcement');
      }
    } catch (error) {
      toast.error('Announcement failed');
    } finally {
      setIsAnnouncing(false);
    }
  };

  const sendPopup = async () => {
    if (!popupForm.message) {
      toast.error('Message is required');
      return;
    }
    
    try {
      await api.post(`/admin/auctions/${auctionId}/popup`, popupForm);
      toast.success('Popup sent to all viewers!');
      setShowPopupForm(false);
      setPopupForm({ title: '', message: '', type: 'info', duration: 10 });
      fetchRoomData();
    } catch (error) {
      toast.error('Failed to send popup');
    }
  };

  const deletePopup = async (popupId) => {
    try {
      await api.delete(`/admin/auctions/${auctionId}/popup/${popupId}`);
      toast.success('Popup removed');
      setPopups(prev => prev.filter(p => p.id !== popupId));
    } catch (error) {
      toast.error('Failed to delete popup');
    }
  };

  const toggleAuctionStatus = async (newStatus) => {
    try {
      await api.put(`/admin/auctions/${auctionId}`, { status: newStatus });
      toast.success(`Auction ${newStatus}`);
      fetchRoomData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-green-500';
      case 'upcoming': return 'bg-blue-500';
      case 'completed': return 'bg-gray-500';
      case 'paused': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getPopupTypeColor = (type) => {
    switch (type) {
      case 'highlight': return 'border-amber-500 bg-amber-500/20';
      case 'warning': return 'border-red-500 bg-red-500/20';
      case 'success': return 'border-green-500 bg-green-500/20';
      default: return 'border-blue-500 bg-blue-500/20';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 text-center">
        <p className="text-xl text-muted-foreground">Auction not found</p>
        <Link to="/admin/auction" className="text-orange-500 hover:underline mt-4 inline-block">
          Back to Auctions
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid="auction-room">
      <audio ref={audioRef} className="hidden" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin/auction" className="text-muted-foreground hover:text-white text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Auctions
            </Link>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Gavel className="w-7 h-7 text-orange-500" />
              Auction Control Room
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAudioEnabled(!audioEnabled)}
              className={`p-2 rounded-lg ${audioEnabled ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}
              title={audioEnabled ? 'Audio On' : 'Audio Off'}
            >
              {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <button onClick={fetchRoomData} className="p-2 rounded-lg bg-white/10 hover:bg-white/20">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Auction Info & Controls */}
          <div className="lg:col-span-2 space-y-6">
            {/* Auction Card */}
            <div className="glass-card p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${getStatusColor(auction.status)}`}>
                      {auction.status?.toUpperCase()}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" /> {connectedUsers} viewing
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold">{auction.title}</h2>
                  <p className="text-muted-foreground mt-1">{auction.description}</p>
                </div>
                {auction.images?.[0] && (
                  <img src={getImageUrl(auction.images[0])} alt="" className="w-24 h-24 rounded-xl object-cover" />
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-sm text-muted-foreground">Current Bid</p>
                  <p className="text-2xl font-bold text-green-500">₹{(auction.current_bid || auction.starting_bid)?.toLocaleString()}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Bids</p>
                  <p className="text-2xl font-bold">{auction.total_bids || 0}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-sm text-muted-foreground">Unique Bidders</p>
                  <p className="text-2xl font-bold">{auction.unique_bidders || 0}</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                {auction.status === 'live' && (
                  <button
                    onClick={() => toggleAuctionStatus('paused')}
                    className="px-4 py-2 bg-yellow-500 text-black rounded-lg flex items-center gap-2 font-medium"
                  >
                    <Pause className="w-4 h-4" /> Pause Auction
                  </button>
                )}
                {auction.status === 'paused' && (
                  <button
                    onClick={() => toggleAuctionStatus('live')}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg flex items-center gap-2 font-medium"
                  >
                    <Play className="w-4 h-4" /> Resume Auction
                  </button>
                )}
                <button
                  onClick={() => setShowPopupForm(true)}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg flex items-center gap-2 font-medium"
                >
                  <MessageSquare className="w-4 h-4" /> Send Popup
                </button>
              </div>
            </div>

            {/* Voice Announcements */}
            <div className="glass-card p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Mic className="w-5 h-5 text-pink-500" />
                Priya Voice Announcements
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <button
                  onClick={() => sendAnnouncement('live')}
                  disabled={isAnnouncing}
                  className="p-3 bg-green-500/20 text-green-500 rounded-xl hover:bg-green-500/30 disabled:opacity-50 text-center"
                >
                  <Play className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">Auction Live</span>
                </button>
                <button
                  onClick={() => sendAnnouncement('ending')}
                  disabled={isAnnouncing}
                  className="p-3 bg-yellow-500/20 text-yellow-500 rounded-xl hover:bg-yellow-500/30 disabled:opacity-50 text-center"
                >
                  <Clock className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">Ending Soon</span>
                </button>
                <button
                  onClick={() => sendAnnouncement('completed')}
                  disabled={isAnnouncing}
                  className="p-3 bg-blue-500/20 text-blue-500 rounded-xl hover:bg-blue-500/30 disabled:opacity-50 text-center"
                >
                  <CheckCircle className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">Completed</span>
                </button>
                <button
                  onClick={() => sendAnnouncement('custom')}
                  disabled={isAnnouncing || !customMessage}
                  className="p-3 bg-pink-500/20 text-pink-500 rounded-xl hover:bg-pink-500/30 disabled:opacity-50 text-center"
                >
                  <Sparkles className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">Custom</span>
                </button>
              </div>
              
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type custom announcement for Priya..."
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                />
                <button
                  onClick={() => sendAnnouncement('custom')}
                  disabled={isAnnouncing || !customMessage}
                  className="px-4 py-3 bg-pink-500 text-white rounded-xl disabled:opacity-50"
                >
                  {isAnnouncing ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
              
              {isAnnouncing && (
                <div className="mt-3 flex items-center gap-2 text-pink-500">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                    <span className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                    <span className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                  </div>
                  <span className="text-sm">Priya is speaking...</span>
                </div>
              )}
            </div>

            {/* Active Popups */}
            {popups.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Active Popups ({popups.length})
                </h3>
                <div className="space-y-3">
                  {popups.map((popup) => (
                    <div key={popup.id} className={`p-4 rounded-xl border-2 ${getPopupTypeColor(popup.type)}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          {popup.title && <h4 className="font-bold">{popup.title}</h4>}
                          <p className="text-sm">{popup.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Duration: {popup.duration}s | Type: {popup.type}
                          </p>
                        </div>
                        <button
                          onClick={() => deletePopup(popup.id)}
                          className="p-1 text-red-500 hover:bg-red-500/20 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Bid Feed */}
          <div className="space-y-6">
            {/* Live Bid Feed */}
            <div className="glass-card p-4">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Live Bid Feed
              </h3>
              
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {bids.length > 0 ? (
                  bids.map((bid, i) => (
                    <div 
                      key={bid.id || i}
                      className={`p-3 rounded-lg ${i === 0 ? 'bg-green-500/20 border border-green-500/30' : 'bg-white/5'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{bid.user?.name || 'Anonymous'}</p>
                          <p className="text-xs text-muted-foreground">@{bid.user?.username || 'user'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-500">₹{bid.amount?.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">
                            {bid.created_at ? formatTimeIST(bid.created_at) + ' IST' : 'Just now'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Gavel className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No bids yet</p>
                    <p className="text-sm">Waiting for bidders...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="glass-card p-4">
              <h3 className="font-bold mb-3">Auction Stats</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Starting Bid</span>
                  <span>₹{auction.starting_bid?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Bid Increment</span>
                  <span>₹{auction.bid_increment?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reserve Price</span>
                  <span>₹{auction.reserve_price?.toLocaleString() || 'None'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Buy Now</span>
                  <span>₹{auction.buy_now_price?.toLocaleString() || 'Disabled'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Entry Pass</span>
                  <span>{auction.entry_pass_tier || 'None'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup Form Modal */}
      {showPopupForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-500" />
              Send Popup to Viewers
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1">Title (optional)</label>
                <input
                  type="text"
                  value={popupForm.title}
                  onChange={(e) => setPopupForm({ ...popupForm, title: e.target.value })}
                  placeholder="Announcement Title"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                />
              </div>
              
              <div>
                <label className="block text-sm mb-1">Message *</label>
                <textarea
                  value={popupForm.message}
                  onChange={(e) => setPopupForm({ ...popupForm, message: e.target.value })}
                  placeholder="Your message to all viewers..."
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Type</label>
                  <select
                    value={popupForm.type}
                    onChange={(e) => setPopupForm({ ...popupForm, type: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  >
                    <option value="info">Info (Blue)</option>
                    <option value="highlight">Highlight (Gold)</option>
                    <option value="warning">Warning (Red)</option>
                    <option value="success">Success (Green)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">Duration (sec)</label>
                  <input
                    type="number"
                    value={popupForm.duration}
                    onChange={(e) => setPopupForm({ ...popupForm, duration: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                    min={3}
                    max={60}
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowPopupForm(false)}
                  className="flex-1 py-3 bg-white/10 rounded-xl font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={sendPopup}
                  className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> Send Popup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionRoom;
